import { useEffect, useRef, useState } from 'react';
import { Info, Loader2, Pencil } from 'lucide-react';

import { SoftCard } from '@/components/ui/soft-card';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { formatInsightNumber, formatMoney, formatWholeMoney } from '@/lib/liaInsights';

const SAVE_FEEDBACK_MS = 2500;

/**
 * Three summary cards above the Clicks-to-sales table (NEA-5963): total
 * attributed sales, total clicks-to-sales revenue, and total basket revenue
 * (projected — sales × the retailer's average basket value). The last card
 * carries an inline editable pill so the retailer can tune the basket estimate
 * without leaving the page.
 *
 * All three numbers come pre-aggregated from `clicksToSalesSummary` on the
 * `/api/local-ads/{shopId}` response — this component is presentation only.
 * Rendered on both the Ads page and the Results page (NEA-6189), so the period
 * caption is a prop rather than a hardcoded "Past 30 days".
 */
export function ClicksToSalesSummary({ summary, currency, onSaveBasketValue, periodLabel = 'Past 30 days' }) {
	const sales = Number(summary?.sales) || 0;
	const revenue = Number(summary?.revenue) || 0;
	const basketRevenue = Number(summary?.basketRevenue) || 0;
	const averageBasketValue = Number(summary?.averageBasketValue) || 45;

	// Save feedback takes over the period caption instead of sitting beside the
	// pill: an inline status pushed the pill cluster leftwards into the caption
	// and rendered on a different baseline.
	const [saveState, setSaveState] = useState(null);

	useEffect(() => {
		if (saveState !== 'saved' && saveState !== 'failed') return undefined;
		const timer = setTimeout(() => setSaveState(null), SAVE_FEEDBACK_MS);
		return () => clearTimeout(timer);
	}, [saveState]);

	const saveBasketValue = async (next) => {
		setSaveState('saving');
		try {
			await onSaveBasketValue?.(next);
			setSaveState('saved');
		} catch {
			setSaveState('failed');
		}
	};

	const basketCaption =
		saveState === 'saving' ? (
			<span className="inline-flex items-center gap-1.5">
				<Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" /> Saving…
			</span>
		) : saveState === 'saved' ? (
			<span className="text-success-soft-foreground">Updated</span>
		) : saveState === 'failed' ? (
			<span className="text-destructive">Couldn&rsquo;t save — try again</span>
		) : (
			periodLabel
		);

	return (
		<TooltipProvider delayDuration={200}>
			<div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-3">
				<SummaryCard label="Sales" value={formatInsightNumber(sales)} helpText={periodLabel} />
				<SummaryCard
					label="Clicks-to-Sales Revenue"
					value={formatMoney(revenue, currency)}
					helpText={periodLabel}
					tooltip="Revenue from shoppers who clicked an ad and then bought the same product in store. (Excluding additional basket sales.)"
				/>
				<SummaryCard
					label="Total Basket Revenue"
					value={formatMoney(basketRevenue, currency)}
					helpText={basketCaption}
					tooltip="This revenue number includes the total average basket value, to include add-on items purchased alongside the clicked product."
					accessory={
						<BasketValuePill
							amount={averageBasketValue}
							currency={currency}
							onSave={saveBasketValue}
							saving={saveState === 'saving'}
						/>
					}
				/>
			</div>
		</TooltipProvider>
	);
}

/**
 * The value steps down to `text-3xl` below `md` and the basket pill is pinned to
 * the card's bottom-right rather than sharing a row with the caption — both come
 * from the updated design (NEA-6190), which a six-figure basket revenue was
 * overflowing at the previous fixed `text-4xl` / two-column footer.
 */
function SummaryCard({ label, value, helpText, tooltip, accessory }) {
	return (
		<SoftCard tone="white" className="relative px-6 py-5">
			<div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
				<span>{label}</span>
				{tooltip ? (
					<Tooltip>
						<TooltipTrigger asChild>
							<button
								type="button"
								aria-label={`About ${label}`}
								className="inline-flex h-4 w-4 items-center justify-center rounded-full text-muted-foreground/70 transition hover:text-foreground"
							>
								<Info className="h-3.5 w-3.5" />
							</button>
						</TooltipTrigger>
						<TooltipContent className="max-w-xs text-left">{tooltip}</TooltipContent>
					</Tooltip>
				) : null}
			</div>
			<div className="mt-3 text-3xl font-extrabold leading-none tracking-tight text-foreground tabular-nums md:text-4xl">{value}</div>
			{accessory}
			<div className="mt-3 text-sm text-muted-foreground" aria-live="polite">
				{helpText}
			</div>
		</SoftCard>
	);
}

/**
 * Inline "£45 ✎" pill on the Total Basket Revenue card. Clicking swaps the
 * amount for a numeric input — Enter or blur commits, Escape reverts — and the
 * save persists via `onSave(newValue)`, which calls `shopApi.updateMyShop` on
 * the way through. While the write is in flight the pill shows the pending
 * amount with a spinner; the outcome is reported in the card's caption.
 */
function BasketValuePill({ amount, currency, onSave, saving }) {
	// The editor is digits-only, so a legacy decimal (the popover this replaced
	// allowed `step=0.01`) must be rounded before it seeds the draft — otherwise
	// one caret-placed keystroke strips the point and commits a 10x value.
	const wholeAmount = Math.round(amount);
	const [editing, setEditing] = useState(false);
	const [draft, setDraft] = useState(String(wholeAmount));
	// The committed-but-not-yet-revalidated amount, so the pill shows what the
	// retailer typed while the request is in flight rather than the old value.
	const [pending, setPending] = useState(null);
	const inputRef = useRef(null);
	// Guards the blur-commit against firing a second time when Enter/Escape has
	// already closed the input (both move focus off it).
	const committed = useRef(false);

	useEffect(() => {
		if (!editing) setDraft(String(wholeAmount));
	}, [editing, wholeAmount]);

	useEffect(() => {
		if (!editing) return;
		// Select on open so typing replaces the current amount rather than
		// appending to it.
		inputRef.current?.focus();
		inputRef.current?.select();
	}, [editing]);

	useEffect(() => {
		if (!saving) setPending(null);
	}, [saving]);

	const startEditing = () => {
		committed.current = false;
		setDraft(String(wholeAmount));
		setEditing(true);
	};

	const commit = async () => {
		if (committed.current) return;
		committed.current = true;
		setEditing(false);
		const parsed = Number(draft);
		if (!Number.isFinite(parsed) || parsed <= 0 || parsed === wholeAmount) return;
		if (!onSave) return;
		setPending(parsed);
		await onSave(parsed);
	};

	const cancel = () => {
		committed.current = true;
		setDraft(String(wholeAmount));
		setEditing(false);
	};

	const displayed = pending ?? amount;

	return (
		<div className="absolute bottom-4 right-5 flex items-center gap-2">
			<span className="text-right text-[10px] font-medium leading-tight tracking-wide text-primary">
				Average
				<br />
				basket value
			</span>
			{editing ? (
				<span className="inline-flex items-center gap-1 rounded-full border border-primary bg-background py-1.5 pl-3 pr-2.5 text-sm font-semibold shadow-sm ring-2 ring-primary/20">
					<input
						ref={inputRef}
						type="text"
						inputMode="numeric"
						aria-label="Average basket value"
						value={draft}
						onChange={(e) => setDraft(e.target.value.replace(/[^0-9]/g, ''))}
						onBlur={commit}
						onKeyDown={(e) => {
							if (e.key === 'Enter') {
								e.preventDefault();
								commit();
							} else if (e.key === 'Escape') {
								e.preventDefault();
								cancel();
							}
						}}
						className="w-12 bg-transparent font-semibold tabular-nums text-foreground outline-none"
					/>
					<Pencil className="h-3.5 w-3.5 text-primary" />
				</span>
			) : (
				<button
					type="button"
					onClick={startEditing}
					disabled={saving}
					aria-label="Edit average basket value"
					aria-busy={saving || undefined}
					className="inline-flex items-center gap-1.5 rounded-full border border-primary bg-background py-1.5 pl-3 pr-2.5 text-sm font-semibold text-foreground transition hover:shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30 disabled:opacity-70"
				>
					<span className="tabular-nums">{formatWholeMoney(displayed, currency)}</span>
					{saving ? (
						<Loader2 className="h-3.5 w-3.5 animate-spin text-primary" />
					) : (
						<Pencil className="h-3.5 w-3.5 text-primary" />
					)}
				</button>
			)}
		</div>
	);
}
